import * as React from 'react';
import './itemActions.scss';
import { useActions } from '.././metadataFormActions';
import { ThreeDots } from '../../../../src/components/Icon/icon';
import { Dropdown } from '../../Dropdown/dropdown';
import {
  formatDate,
  getFilteredDataSourcesAndProjects,
  getItemContentTypeId,
  getItemStatusId,
  getItemType,
  getReferenceValue,
  isChildContextualization,
  isKeepActiveEnabled,
  isUpdateDisabled,
  openBusinessCaseModal
} from '../../../utils';
import { ItemStatus } from '../../../enums/ItemStatus';
import { useFullStory } from '../../App/fullStory';
import { NodeType } from '../../../enums/NodeType';
import { ContextualizeModal } from '../Contextualize/contextualizeModal';
import { FSEvent } from '../../../enums/FSEvent';
import { ItemUpdateModal } from '../../ItemUpdateModal/itemUpdateModal';
import { getUpdater } from '../../MetadataForm/metadataForm';
import { ItemKeepActiveModal } from '../../ItemKeepActiveModal/itemKeepActiveModal';
import { useSelector } from '../../../utils/useSelector';
import { ContentType } from '../../../enums/ContentType';
import { RetireModal } from '../../RetireModal/retireModal';
import { getSchemaForm } from '../../../utils';
import { ItemType } from '../../../enums/ItemType';
import { Metadata, MetadataProps, Schema } from '../../../types/Metadata';
import { FieldType } from '../../../enums/FieldType';
import { DataType } from '../../../enums/DataType';
import { DataSource } from '../../../enums/DataSource';
import URLManager from '../../../utils/urlManager';
import { AppUser } from '../../App/appReducer';
import { Role } from '../../../enums/Role';
import { ContentBusinessCaseModal } from '../ConfigureWorkflowField/contentBusinessCaseModal';

enum Action {
  Validate = 'Validate',
  Contextualize = 'Contextualize',
  Update = 'Update',
  Clone = 'Clone',
  KeepActive = 'Keep Active',
  Retire = 'Retire',
  CreateFrom = 'Create From',
  CreateConfSessionIdea = 'Create Conference Session Idea',
  BusinessCase = 'Content Business Case'
}

type Props = { disabled: boolean };

export const ItemActions = ({ disabled }: Props) => {
  const data = useSelector(s => s.form?.data);
  const schema = useSelector(s => s.form?.schema);
  const currentUser = useSelector(s => s.app?.user);
  const actionEmployeeId = useSelector(
    s => s.view?.actingAs?.props?.empCd || s.app?.user?.employeeId
  );
  const backingUpFor = useSelector(s => s.view?.backingUpFor) || null;
  const adminApproval =
    useSelector(s =>
      s.view?.actingAs != null
        ? false
        : s.app?.user?.roles?.includes(Role.admin)
    ) &&
    process.env.ENVIRONMENT != 'PROD' &&
    process.env.ENVIRONMENT != 'DEVB';
  const schemaForm = getSchemaForm(schema, data);
  const [selectedAction, setSelectedAction] = React.useState<Action>(null);
  const {
    validate,
    updateField,
    updateData,
    updateFields,
    setPendingRetirement,
    showForm
  } = useActions();
  const { updateUrl } = URLManager.useRouter();
  const { captureEvent } = useFullStory();
  const statusId = getItemStatusId(data);
  const contentTypeId = getItemContentTypeId(data);
  const hasUniRelationship =
    data?.props?.uniBy?.length > 0 || data?.props?.uniOf?.length > 0;
  const refId = data?.props?.refId;
  const itemType = getItemType(data);
  const itemContentTypeId = getItemContentTypeId(data);
  const closeModal = React.useMemo(() => () => setSelectedAction(null), []);

  const shouldShowBusinessCase = openBusinessCaseModal(
    data,
    backingUpFor,
    actionEmployeeId,
    adminApproval
  );

  const options = [
    Action.Validate,
    Action.Contextualize,
    Action.Update,
    Action.KeepActive,
    Action.Retire
  ];

  if (shouldDisplayCloneOption(data) && !options.includes(Action.Clone)) {
    options.splice(1, 0, Action.Clone);
  }
  if (
    shouldDisplayCreateFromOption(data) &&
    !options.includes(Action.CreateFrom)
  ) {
    options.splice(1, 0, Action.CreateFrom);
  }
  if (
    shouldDisplayCreateConferenceSubmissionOption(data) &&
    !options.includes(Action.CreateConfSessionIdea)
  ) {
    options.splice(1, 0, Action.CreateConfSessionIdea);
  }
  // if (shouldShowBusinessCase && !options.includes(Action.BusinessCase)) {
  //   options.push(Action.BusinessCase);
  // }

  const isActionDisabled = React.useCallback(
    (action: Action) => {
      if (disabled) return true;
      if (
        itemType == ItemType.conferenceSession &&
        action != Action.CreateFrom
      ) {
        return true;
      }
      switch (action) {
        case Action.Retire:
          return (
            contentTypeId != ContentType.MAGIC_QUADRANT ||
            !hasUniRelationship ||
            ![
              ItemStatus.Concept_Review,
              ItemStatus.Idea,
              ItemStatus.Not_Pursued
            ].includes(statusId) ||
            itemType != ItemType.content
          );
        case Action.Validate:
          return (
            ![ItemStatus.Concept_Review, ItemStatus.Idea].includes(statusId) ||
            ![ItemType.content, ItemType.globalSubmission].includes(itemType)
          );
        case Action.Update:
          return isUpdateDisabled(data);
        case Action.Contextualize:
          if (
            (statusId < ItemStatus.Outline &&
              ![
                ItemStatus.Complete,
                ItemStatus.PublishedHistoricalNonCPP
              ].includes(statusId)) ||
            isChildContextualization(data) ||
            !data?.props.contentType?.length ||
            itemType != ItemType.content ||
            itemContentTypeId == ContentType.Executive_FastStart
          ) {
            return true;
          }
          break;
        case Action.KeepActive:
          if (!isKeepActiveEnabled(data)) {
            return true;
          }
          break;
      }
      return false;
    },
    [statusId, contentTypeId, hasUniRelationship, refId]
  );
  const handleItemAction = React.useCallback(
    (action: Action) => {
      if (action == Action.Validate) {
        validate();
      } else if (action == Action.Clone) {
        updateUrl({ id: 'newitem' });
        showForm(getCloneItemData(itemType, data, schemaForm), schema);
      } else if (action == Action.CreateFrom) {
        updateUrl({ id: 'newitem' });
        showForm(getCreateFromItemData(data, schema, currentUser), schema);
      } else if (action == Action.CreateConfSessionIdea) {
        updateUrl({ id: 'newitem' });
        showForm(
          getCreateConfSubmissionItemData(data, schema, currentUser),
          schema
        );
      } else {
        setSelectedAction(action);
      }
    },
    [statusId, data]
  );
  const updater = getUpdater(
    { updateField, updateData, updateFields },
    !!data?.id
  );
  React.useEffect(() => {
    updater.reset();
  }, []);
  if (!data?.labels?.includes(NodeType.Item)) return null;

  return (
    <>
      <Dropdown
        position="right"
        className="item-actions"
        options={options}
        disabled={isActionDisabled}
        onSelect={handleItemAction}
        onStateChanged={expanded => {
          if (expanded) captureEvent(FSEvent.SlideoutThreeDots);
        }}
      >
        <div className="three-dots">
          <ThreeDots />
        </div>
      </Dropdown>
      {selectedAction == Action.Update && (
        <ItemUpdateModal
          data={data}
          schema={schema}
          schemaForm={schemaForm}
          onCancel={closeModal}
        />
      )}
      {selectedAction == Action.Contextualize && (
        <ContextualizeModal
          data={data}
          schema={schema}
          schemaForm={schemaForm}
          onCancel={closeModal}
        />
      )}
      {selectedAction == Action.KeepActive && (
        <ItemKeepActiveModal data={data} onCancel={closeModal} />
      )}
      {selectedAction == Action.Retire && (
        <RetireModal
          data={data}
          onCancel={closeModal}
          onSubmit={async p => {
            await setPendingRetirement(data, p);
            closeModal();
          }}
        />
      )}
      {selectedAction == Action.BusinessCase && (
        <ContentBusinessCaseModal
          isOpen={true}
          onClose={closeModal}
          data={data}
          updater={updater}
        />
      )}
    </>
  );
};

const shouldDisplayCloneOption = (data: Partial<Metadata>) =>
  !!data &&
  [ItemType.globalSubmission, ItemType.content].includes(getItemType(data));

const shouldDisplayCreateFromOption = (data: Partial<Metadata>) =>
  !!data && getItemType(data) == ItemType.conferenceSession;

const shouldDisplayCreateConferenceSubmissionOption = (
  data: Partial<Metadata>
) => data && getItemType(data) === ItemType.content;

const cloneExcludeFields = [
  'refId',
  'createdBy',
  'modifiedBy',
  'itemId',
  'configureWorkflowType',
  'configureWorkflowByEmpCd',
  'configureWorkflowSkipIds',
  'peerReviewer'
];

const getCloneItemData = (
  itemType: ItemType,
  data: Partial<Metadata>,
  schemaForm: Schema
) => {
  const cloneFieldData = schemaForm?.props?.fields
    ?.filter(
      f =>
        !f?.isSystemField ||
        (f?.isSystemField &&
          f.fieldType != FieldType.computed &&
          !f.readOnly &&
          f.parentField != 'relationship')
    )
    ?.filter(f => !cloneExcludeFields.includes(f.field))
    ?.reduce((d, f) => {
      if (!data?.props) return d;
      if (f.field == 'dataSourceProjects' && data.props.dataSource?.length) {
        const {
          filteredDataSources,
          filteredProjects,
          filteredDataSourceItems
        } = getFilteredDataSourcesAndProjects(data);

        d.dataSource = filteredDataSources;
        d.project = filteredProjects;
        d.dataSourceItem = filteredDataSourceItems;
      } else if (!!data?.props?.[f.field]) {
        if (f?.dataType == DataType.date || f?.dataType == DataType.dateTime) {
          d[f.field] = formatDate(data?.props?.[f.field], true);
        } else if (f.field == 'title') {
          d.title = `Clone - ${data.props.title}`;
        } else {
          d[f.field] = data?.props?.[f.field];
        }
      }
      return d;
    }, {} as MetadataProps);

  const clone = {
    props: {
      ...cloneFieldData,
      cloneOf: [{ props: { refId: data?.props?.refId } }],
      status: [
        {
          id: '1',
          labels: ['Status'],
          props: {
            description: 'Idea',
            id: 1,
            refId: 'status:1'
          }
        }
      ],
      itemType
    }
  };
  return clone;
};

const getCreateFromFields = (schema: Schema, hasKIInfo: boolean) => {
  // CN-3362: Only copy specific fields from Conference Session to Global Submission
  const baseFields = [
    'title',
    'summary',
    'confSessionType',
    'speakerBench',
    'dataSourceProjects',
    'dataSource',
    'project',
    'dataSourceItem',
    'disrupterTopic'
  ];

  // Audience fields - only include if there is NO KI info
  const audienceFields = [
    'function',
    'role',
    'topicCategory',
    'taxonomyTag',
    'additionalFunctions',
    'additionalRoles',
    'additionalTopicCategory',
    'additionalTaxonomyTag',
    'priorityJourneyStep'
  ];

  // If KI info exists, exclude audience fields; otherwise include them
  return hasKIInfo ? baseFields : [...baseFields, ...audienceFields];
};

const getCreateFromItemData = (
  data: Partial<Metadata>,
  schema: Schema,
  currentUser: AppUser
) => {
  const schemaForm = getSchemaForm(schema, data);
  const isPersonInBench = (bench, person) =>
    bench?.some(b => b?.props?.empCd === person?.props?.empCd);

  const hasKIInfo =
    getReferenceValue(data, 'keyInitiative') != null ||
    getReferenceValue(data, 'additionalKeyInitiatives') != null ||
    getReferenceValue(data, 'coreTopic') != null;

  const allowedFields = getCreateFromFields(schema, hasKIInfo);

  let createFromFieldData = schemaForm?.props?.fields
    ?.filter(f => allowedFields.includes(f.field))
    ?.reduce((d, f) => {
      if (!data?.props) return d;
      if (f.field == 'dataSourceProjects' && data.props.dataSource?.length) {
        const {
          filteredDataSources,
          filteredProjects,
          filteredDataSourceItems
        } = getFilteredDataSourcesAndProjects(data);
        d.dataSource = filteredDataSources;
        d.project = filteredProjects;
        d.dataSourceItem = filteredDataSourceItems;
      } else if (!!data?.props?.[f.field]) {
        if (f.field === 'title') {
          d.title = `Created From - ${data.props.title}`;
        } else {
          d[f.field] = data?.props?.[f.field];
        }
      }
      return d;
    }, {} as MetadataProps);
  createFromFieldData.author = [
    {
      props: {
        empCd: currentUser.employeeId,
        empEmail: currentUser.email,
        empFstNm: currentUser.firstName,
        empLstNm: currentUser.lastName,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        isActive: true,
        refId: `emp:${currentUser.employeeId}`
      },
      labels: ['Employee']
    }
  ];

  const speakerBench = Array.isArray(createFromFieldData.speakerBench)
    ? [...createFromFieldData.speakerBench]
    : [];

  (Array.isArray(data?.props?.author) ? data.props.author : []).forEach(
    author => {
      if (!isPersonInBench(speakerBench, author)) {
        speakerBench.push(author);
      }
    }
  );
  (Array.isArray(data?.props?.coAuthors) ? data.props.coAuthors : []).forEach(
    coAuthor => {
      if (!isPersonInBench(speakerBench, coAuthor)) {
        speakerBench.push(coAuthor);
      }
    }
  );

  createFromFieldData.speakerBench = speakerBench.filter(
    person => person?.props?.isActive != false
  );

  const createdFrom = {
    props: {
      ...createFromFieldData,
      cloneOf: [{ props: { refId: data?.props?.refId } }],
      itemType: ItemType.globalSubmission
    }
  };

  return createdFrom;
};

const getCreateConfSubmissionFields = () => {
  const fields = [
    'title',
    'summary',
    'function',
    'role',
    'topicCategory',
    'additionalTopicCategory',
    'taxonomyTag',
    'additionalTaxonomyTag',
    'rolePriority',
    'priorityJourneyStep',
    'disrupterTopic'
  ];
  return fields;
};

const getCreateConfSubmissionItemData = (
  data: Partial<Metadata>,
  schema: Schema,
  currentUser: AppUser
) => {
  const schemaForm = getSchemaForm(schema, data);
  const isPersonInBench = (bench: Metadata[], person: Metadata) =>
    bench?.some(b => b?.props?.empCd === person?.props?.empCd);

  const hasFeaturedInsights: boolean =
    data?.props?.primaryRolePriority?.length > 0 &&
    data?.props?.primaryPriorityJourneyStep?.length > 0;
  const allowedFields = getCreateConfSubmissionFields();

  const fieldData =
    schemaForm?.props?.fields
      ?.filter(f => allowedFields.includes(f.field))
      ?.reduce((d, f) => {
        if (!data?.props) return d;
        if (data?.props?.[f.field]) {
          if (f.field === 'title') {
            d.title = `Created From - ${data.props.title}`;
          } else {
            d[f.field] = data?.props?.[f.field];
          }
        }
        return d;
      }, {} as MetadataProps) || ({} as MetadataProps);

  if (hasFeaturedInsights) {
    const merge = (existingData?: Metadata[], incomingData?: Metadata[]) => {
      if (!incomingData?.length) return existingData ?? [];
      const strippedData = (incomingData ?? []).map(
        ({ relationship, ...rest }) => rest
      );
      const combinedData = [...(existingData ?? []), ...strippedData];
      return combinedData.filter(
        (other, idx, self) =>
          self.findIndex(
            original => original?.props?.refId === other?.props?.refId
          ) === idx
      );
    };
    fieldData.rolePriority = merge(
      fieldData.rolePriority,
      data?.props?.primaryRolePriority
    );
    fieldData.priorityJourneyStep = merge(
      fieldData.priorityJourneyStep,
      data?.props?.primaryPriorityJourneyStep
    );
  }

  fieldData.author = [
    {
      props: {
        empCd: currentUser.employeeId,
        empEmail: currentUser.email,
        empFstNm: currentUser.firstName,
        empLstNm: currentUser.lastName,
        name: `${currentUser.firstName} ${currentUser.lastName}`,
        isActive: true,
        refId: `emp:${currentUser.employeeId}`
      },
      labels: ['Employee']
    }
  ];

  const speakerBench: Metadata[] = [];
  (Array.isArray(data?.props?.author) ? data.props.author : []).forEach(
    author => {
      if (!isPersonInBench(speakerBench, author)) {
        speakerBench.push(author);
      }
    }
  );
  (Array.isArray(data?.props?.coAuthors) ? data.props.coAuthors : []).forEach(
    coAuthor => {
      if (!isPersonInBench(speakerBench, coAuthor)) {
        speakerBench.push(coAuthor);
      }
    }
  );
  fieldData.speakerBench = speakerBench.filter(
    person => person?.props?.isActive != false
  );

  fieldData.dataSource = [
    {
      labels: ['DataSource'],
      props: {
        id: DataSource.PublishedGartnerResearch,
        name: 'Previously Published Gartner Research',
        refId: `ds:${DataSource.PublishedGartnerResearch}`
      }
    }
  ];
  fieldData.dataSourceItem = [
    {
      labels: ['Item'],
      props: {
        itemId: data.props.itemId,
        title: data.props.title,
        refId: data.props.refId
      }
    }
  ];

  return {
    props: {
      ...fieldData,
      cloneOf: [{ props: { refId: data?.props?.refId } }],
      status: [
        {
          id: '1',
          labels: ['Status'],
          props: {
            description: 'Idea',
            id: 1,
            refId: 'status:1'
          }
        }
      ],
      itemType: ItemType.globalSubmission
    }
  };
};
